<script setup>
import { onMounted, ref, inject, computed } from "vue";
import { router, usePage, Link } from "@inertiajs/vue3";
const page = usePage();

const locale = computed(() => {
    return page.props.locale;
});

// Translation helper function
const __t = inject("__t");

defineProps({
    transparentMode: {
        type: Boolean,
        default: false
    }
});

// Static menu items
const staticMenuItems = computed(() => [
    {
        label: { ar: __t('home'), en: __t('home'), ru: __t('home'), tr: __t('home'), es: __t('home'), fr: __t('home') },
        url: 'home',
        child: []
    },
    {
        label: { ar: __t('about_us'), en: __t('about_us'), ru: __t('about_us'), tr: __t('about_us'), es: __t('about_us'), fr: __t('about_us') },
        url: 'about',
        child: []
    },
    {
        label: { ar: __t('products'), en: __t('products'), ru: __t('products'), tr: __t('products'), es: __t('products'), fr: __t('products') },
        url: 'products',
        child: []
    },
    {
        label: { ar: __t('Projects'), en: __t('Projects'), ru: __t('Projects'), tr: __t('Projects'), es: __t('Projects'), fr: __t('Projects') },
        url: 'projects',
        child: []
    },
    {
        label: { ar: __t('events'), en: __t('events'), ru: __t('events'), tr: __t('events'), es: __t('events'), fr: __t('events') },
        url: 'events',
        child: []
    },
  
    // {
    //     label: { ar: __t('sales_centers'), en: __t('sales_centers'), ru: __t('sales_centers'), tr: __t('sales_centers') },
    //     url: 'sales-centers',
    //     child: []
    // },
    // {
    //     label: { ar: 'الحلول', en: 'Solutions', ru: 'Solutions', tr: 'Solutions' },
    //     url: 'solutions',
    //     child: []
    // },
    // {
    //     label: { ar: 'الاستدامة', en: 'Sustainability', ru: 'Sustainability', tr: 'Sustainability' },
    //     url: 'sustainability',
    //     child: []
    // },
    {
        label: { ar: __t('iso-certifications'), en: __t('iso-certifications'), ru: __t('iso-certifications'), tr: __t('iso-certifications'), es: __t('iso-certifications'), fr: __t('iso-certifications') },
        url: 'iso-certifications',
        child: []
    },
    {
        label: { ar: __t('News'), en: __t('News'), ru: __t('News'), tr: __t('News'), es: __t('News'), fr: __t('News') },
        url: 'blog',
        child: []
    },
      {
        label: { ar: __t('careers'), en: __t('careers'), ru: __t('careers'), tr: __t('careers'), es: __t('careers'), fr: __t('careers') },
        url: 'careers',
        child: []
    },

]);

const navMenu = ref([]);
const menuStates = ref({});

const loading = ref(true);
onMounted(() => {
    axios
        .get(route("api.headerMenu"))

        .then((data) => {
            navMenu.value = data.data.menu.items;
            navMenu.value.forEach((item, index) => {
                menuStates.value[index] = false; // Initially, all menus are closed
            });
        })
        .catch((error) => { })
        .finally(() => {
            loading.value = false;
        });
});

// Function to check if a URL is internal
const isInternalUrl = (url) => {
    if (!url) return false;
    if (route().has(url)) return true;

    const appUrl = import.meta.env.VITE_APP_URL;
    if (!appUrl) return false;

    // Handle URLs that might be route names or direct URLs
    if (url.includes('|') && url.split('|')[1]) {
        return true; // Posts are internal
    }

    // Check if the URL starts with our domain
    if (typeof url === 'string') {
        return url.startsWith(appUrl) || url.startsWith('/');
    }

    return false;
};

// Function to get URL from item
const getUrl = (item) => {
    if (item.func === 'post' && item.url.split('|')[1]) {
        return route('post.one', (item.url.split('|')[1]));
    } else if (route().has(item.url)) {
        return route(item.url);
    }
    return item.url;
};
</script>
<template>
    <div class="flex flex-row items-center h-full gap-5">
        <!-- Static Menu Items -->
        <div class="" v-for="(item, index) in staticMenuItems" :key="'static-' + index">
            <div v-if="item.child.length == 0"
                class="-mx-2 transition-all cursor-pointer hover:text-secondary py-3 white-text-header">
                <!-- Use Link for internal URLs -->
                <Link v-if="item.url !== '#'" :href="route(item.url)" class="contents">
                    <div
                        class="px-4 py-2 cursor-pointer transition-colors duration-200 hover:bg-black/5 dark:hover:bg-white/10 rounded-md">
                        <div class="text-[15px] font-medium"
                            :class="transparentMode ? 'text-white' : 'text-black dark:text-white'">
                            {{ item.label[locale] }}
                        </div>
                    </div>
                </Link>
            </div>

            <v-menu v-else v-model="menuStates['static-' + index]" :close-on-content-click="true" location="bottom"
                open-on-hover>
                <template v-slot:activator="{ props }">
                    <div class="-mx-2 transition-all cursor-pointer hover:text-secondary py-3">
                        <v-btn variant="text" v-bind="props">
                            <div class="text-black dark:text-white text-[15px]">
                                {{ item.label[locale] }}
                            </div>
                        </v-btn>
                    </div>
                </template>

                <v-card class="mt-2 bg-white w-[200px] overflow-hidden">
                    <v-row class="p-4 px-4 overflow-hidden">
                        <v-col cols="12" v-for="(child, childIndex) in item.child" :key="'static-child-' + childIndex">
                            <Link :href="route(child.url)" class="self-start lg:self-start text-start">
                                <div variant="text"
                                    class="flex flex-col items-center mb-2 text-start hover:bg-[surface]">
                                    <div class="text-black dark:text-white text-[15px] w-full text-start">
                                        {{ child.label[locale] }}
                                    </div>
                                </div>
                            </Link>
                        </v-col>
                    </v-row>
                </v-card>
            </v-menu>
        </div>

        <!-- Dynamic Menu Items -->
        <div class="" v-for="(item, index) in navMenu" :key="'dynamic-' + index">
            <div v-if="item.child.length == 0"
                class="-mx-2 transition-all cursor-pointer hover:text-secondary py-3 white-text-header">
                <!-- Use Link for internal URLs and a for external URLs -->
                <Link v-if="isInternalUrl(item.url)" :href="getUrl(item)" class="contents">
                    <div
                        class="px-4 py-2 cursor-pointer transition-colors duration-200 hover:bg-black/5 dark:hover:bg-white/10 rounded-md">
                        <div class="text-black dark:text-white text-[15px] font-medium">
                            {{ item.lable[locale] }}
                        </div>
                    </div>
                </Link>
                <div v-else class="contents">
                    <a :href="getUrl(item)"
                        class="px-4 py-2 cursor-pointer transition-colors duration-200 hover:bg-black/5 dark:hover:bg-white/10 rounded-md inline-block">
                        <div class="text-black dark:text-white text-[15px] font-medium">
                            {{ item.lable[locale] }}
                        </div>
                    </a>
                </div>
            </div>

            <v-menu v-else v-model="menuStates['dynamic-' + index]" :close-on-content-click="true" location="bottom"
                open-on-hover>
                <template v-slot:activator="{ props }">
                    <div class="-mx-2 transition-all cursor-pointer hover:text-secondary py-3">
                        <v-btn variant="text" v-bind="props">
                            <div class=" text-black dark:text-white text-[15px]">
                                {{ item.lable[locale] }}
                            </div>
                        </v-btn>
                    </div>
                </template>

                <v-card class="mt-2 bg-white w-[200px] overflow-hidden">
                    <v-row class="p-4 px-4 overflow-hidden ">
                        <v-col cols="12" v-for="child in item.child" :key="child.lable">
                            <!-- Use Link for internal URLs and a for external URLs -->
                            <Link v-if="isInternalUrl(child.url)" :href="getUrl(child)"
                                class="self-start lg:self-start text-start ">
                                <div variant="text"
                                    class="flex flex-col items-center mb-2  text-start hover:bg-[surface]">
                                    <div class=" text-black dark:text-white text-[15px] w-full text-start">
                                        {{ child.lable[locale] }}
                                    </div>
                                </div>
                            </Link>
                            <a v-else :href="getUrl(child)" class="self-start lg:self-start text-start ">
                                <div variant="text"
                                    class="flex flex-col items-center mb-2  text-start hover:bg-[surface]">
                                    <div class=" text-black dark:text-white text-[15px] w-full text-start">
                                        {{ child.lable[locale] }}
                                    </div>
                                </div>
                            </a>
                        </v-col>
                    </v-row>
                </v-card>
            </v-menu>
        </div>

        <div class="flex" v-if="loading">
            <div class="flex items-center h-full" v-for="item in 4" :key="item">
                <div class="flex items-center -mx-2 bg-transparent">
                    <v-skeleton-loader :loading="true" class="w-[100px] p-0 m-0 bg-transparent" type="button">
                        <v-list-item class="w-[200px]" lines="tow" subtitle="Subtitle" title="Title"
                            rounded></v-list-item>
                    </v-skeleton-loader>
                </div>
            </div>
        </div>
    </div>
</template>
<style>
:deep(.v-skeleton-loader__button) {
    border-radius: 4px;
    height: 36px;
    margin: 1px;
    max-width: 100%;
}
</style>
